import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import client, { fullDate, formatDate, formatViews } from "../lib/api";
import { useSeo } from "../lib/useSeo";
import Layout, { Spinner } from "../components/Layout";
import { NewsCard } from "../components/NewsCard";
import { SectionHeader } from "../components/SectionBlock";
import { CAT_LABEL } from "../lib/categories";
import { Facebook, Twitter, Send, Share2, AlertTriangle, User, Eye, Link2, PlayCircle } from "lucide-react";
import { toast } from "sonner";

export default function Article() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setData(null); setNotFound(false);
    window.scrollTo(0, 0);
    client.get(`/articles/${slug}`)
      .then((r) => {
        setData(r.data);
        // anti-abuse: count a genuine view only once per article per browser session
        const seen = JSON.parse(sessionStorage.getItem("r24_viewed") || "[]");
        if (!seen.includes(slug)) {
          client.post(`/articles/${slug}/view`).catch(() => {});
          sessionStorage.setItem("r24_viewed", JSON.stringify([...seen, slug]));
        }
      })
      .catch(() => setNotFound(true));
  }, [slug]);

  const art = data?.article;
  useSeo(art ? {
    title: art.seo_title || art.title,
    description: art.meta_description || art.excerpt,
    image: art.featured_image,
    type: "article",
    article: art,
    breadcrumb: [
      { name: "Home", url: window.location.origin },
      { name: CAT_LABEL[art.category]?.name || art.category, url: `${window.location.origin}/category/${art.category}` },
      { name: art.title, url: window.location.href },
    ],
  } : {});

  if (notFound) return <Layout><div className="max-w-3xl mx-auto px-4 py-24 text-center"><h1 className="font-head text-3xl font-bold">Article not found</h1><Link to="/" className="text-r24-red font-semibold mt-4 inline-block">← Back to Home</Link></div></Layout>;
  if (!data) return <Layout><Spinner /></Layout>;

  const hi = art.language === "hi";
  const cat = CAT_LABEL[art.category];
  const url = window.location.href;
  const shareText = encodeURIComponent(art.title + " | Rapid24News");
  const shares = [
    { icon: <Send size={18} />, label: "WhatsApp", href: `https://wa.me/?text=${shareText}%20${encodeURIComponent(url)}`, cls: "bg-[#25D366]" },
    { icon: <Facebook size={18} />, label: "Facebook", href: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`, cls: "bg-[#1877F2]" },
    { icon: <Twitter size={18} />, label: "X", href: `https://twitter.com/intent/tweet?text=${shareText}&url=${encodeURIComponent(url)}`, cls: "bg-black" },
    { icon: <Send size={18} />, label: "Telegram", href: `https://t.me/share/url?url=${encodeURIComponent(url)}&text=${shareText}`, cls: "bg-[#0088cc]" },
  ];

  return (
    <Layout>
      <article className="max-w-3xl mx-auto px-4 py-6" data-testid="article-page">
        {/* breadcrumb */}
        <nav className="text-xs text-gray-500 mb-4 font-mono2" data-testid="breadcrumbs">
          <Link to="/" className="hover:text-r24-red">Home</Link> / <Link to={`/category/${art.category}`} className="hover:text-r24-red">{cat?.name || art.category}</Link>
        </nav>

        <Link to={`/category/${art.category}`} className="kicker text-xs" data-testid="article-category">{hi && cat ? cat.name_hi : cat?.name || art.category}</Link>
        <h1 className={`font-head font-bold text-3xl md:text-4xl lg:text-5xl leading-[1.08] mt-2 ${hi ? "lang-hi" : ""}`} data-testid="article-title">{art.title}</h1>
        {art.subtitle && <p className={`text-lg text-gray-600 mt-3 ${hi ? "lang-hi" : ""}`} data-testid="article-subtitle">{art.subtitle}</p>}

        <div className="flex flex-wrap items-center gap-x-4 gap-y-2 mt-5 pb-4 border-b border-gray-200 text-sm">
          <Link to={`/author/${art.author_slug}`} className="flex items-center gap-2 font-semibold hover:text-r24-red" data-testid="article-author">
            <User size={16} /> {art.author}
          </Link>
          {art.location && <span className="text-gray-500">📍 {art.location}</span>}
          <span className="text-gray-500 font-mono2">{fullDate(art.published_at)}</span>
          {formatViews(art.views) && (
            <span className="text-gray-500 font-mono2 flex items-center gap-1" data-testid="article-views"><Eye size={15} /> {formatViews(art.views)} Views</span>
          )}
        </div>

        {/* share */}
        <div className="flex items-center gap-2 mt-4" data-testid="share-buttons">
          <span className="text-xs text-gray-500 uc font-semibold flex items-center gap-1"><Share2 size={14} /> Share:</span>
          {shares.map((s) => (
            <a key={s.label} href={s.href} target="_blank" rel="noopener noreferrer" aria-label={s.label} data-testid={`share-${s.label.toLowerCase()}`}
              className={`${s.cls} text-white w-9 h-9 flex items-center justify-center hover:opacity-85`}>{s.icon}</a>
          ))}
          <button onClick={() => { navigator.clipboard?.writeText(url); toast.success("Link copied"); }} aria-label="Copy link" data-testid="share-copy"
            className="bg-gray-700 text-white w-9 h-9 flex items-center justify-center hover:opacity-85"><Link2 size={18} /></button>
        </div>

        {art.featured_image && (
          <figure className="mt-6">
            <img src={art.featured_image} alt={art.title} className="w-full object-cover" data-testid="article-image" />
            {art.image_credit && <figcaption className="text-xs text-gray-500 mt-2">Photo: {art.image_credit}</figcaption>}
          </figure>
        )}

        {art.video_url && (
          <div className="mt-6 aspect-video bg-black">
            <iframe src={art.video_url} title={art.title} className="w-full h-full" allowFullScreen data-testid="article-video" />
          </div>
        )}

        {art.editorial_note && (
          <div className="mt-6 bg-red-50 border-l-4 border-r24-red p-4 flex gap-3" data-testid="editorial-note">
            <AlertTriangle className="text-r24-red shrink-0" size={20} />
            <p className={`text-sm text-gray-700 ${hi ? "lang-hi" : ""}`}>{art.editorial_note}</p>
          </div>
        )}

        <div className={`article-body mt-6 ${hi ? "lang-hi" : ""}`} data-testid="article-body">
          {(art.body || []).map((p, i) => <p key={i}>{p}</p>)}
        </div>

        {Array.isArray(art.timeline) && art.timeline.filter((t) => t && (t.label || t.note)).length > 0 && (
          <section className="mt-8 border border-gray-200 p-5" data-testid="article-timeline">
            <h2 className="font-head font-bold uc text-lg mb-4 border-l-4 border-r24-red pl-3">Story Timeline <span className="font-hindi text-gray-400 text-sm">घटनाक्रम</span></h2>
            <ol className="relative border-l-2 border-gray-200 ml-2">
              {art.timeline.filter((t) => t && (t.label || t.note)).map((t, i) => (
                <li key={i} className="ml-5 pb-5 last:pb-0" data-testid={`timeline-stage-${i}`}>
                  <span className="absolute -left-[7px] w-3 h-3 rounded-full bg-r24-red" />
                  <div className="flex flex-wrap items-baseline gap-2">
                    <span className="font-head font-bold text-sm uc">{t.label || t.stage}</span>
                    {t.date && <span className="text-xs text-gray-500 font-mono2">{t.date}</span>}
                  </div>
                  {t.note && <p className={`text-sm text-gray-700 mt-1 ${hi ? "lang-hi" : ""}`}>{t.note}</p>}
                </li>
              ))}
            </ol>
            <p className="text-[11px] text-gray-400 mt-3 italic">Only verified developments are listed. Allegations are distinguished from confirmed facts.</p>
          </section>
        )}

        {art.tags?.length > 0 && (
          <div className="flex flex-wrap gap-2 mt-8" data-testid="article-tags">
            {[...new Set(art.tags)].map((t) => (
              <Link key={t} to={`/search?q=${encodeURIComponent(t)}`} className="bg-gray-100 hover:bg-r24-red hover:text-white text-xs font-semibold px-3 py-1.5 uc transition-colors">#{t}</Link>
            ))}
          </div>
        )}

        {art.source_url && (
          <p className="text-xs text-gray-500 mt-5">Source / Attribution: <a href={art.source_url} target="_blank" rel="noopener noreferrer" className="text-r24-red underline">{art.source_name || art.source_url}</a></p>
        )}
      </article>

      {data.related?.length > 0 && (
        <section className="max-w-7xl mx-auto px-4 py-10 border-t border-gray-200 mt-6">
          <SectionHeader title="Related Stories" titleHi="संबंधित ख़बरें" to={`/category/${art.category}`} />
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-8">
            {data.related.slice(0, 4).map((a) => <NewsCard key={a.slug} a={a} />)}
          </div>
        </section>
      )}
    </Layout>
  );
}
